import { useEffect, useRef } from 'react';

/**
 * useAutoGrowTextarea — grow a textarea to fit its content, between a min and
 * a max height, scrolling only once the max is reached.
 *
 * This behaviour was already living inline in four places when this hook was
 * written (FormConnectionNote, PillInput, ChatInputNormal, PostComposerSheet).
 * Rather than add a fifth copy for the own-profile "What I'm up to" field, the
 * logic lives here once. The four existing copies are logged as cleanup in the
 * plan's Future Work — they are not this branch's output, so they are left
 * alone rather than churned mid-branch.
 *
 * Returns a ref to attach to the <textarea>. Resizing runs whenever `value`
 * changes, so it works for controlled inputs and for values restored from
 * storage on mount.
 *
 * @param {string} value - the textarea's current value
 * @param {Object} [options]
 * @param {number} [options.minHeight=24] - never shrink below this (px)
 * @param {number} [options.maxHeight=200] - never grow past this; scroll instead (px)
 * @returns {React.RefObject} ref for the textarea element
 */
const useAutoGrowTextarea = (value, { minHeight = 24, maxHeight = 200 } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const textarea = ref.current;
    if (!textarea) return;

    // Reset to auto first — scrollHeight only ever reports the current
    // (larger) height, so without this the field can grow but never shrink.
    textarea.style.height = 'auto';
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [value, minHeight, maxHeight]);

  return ref;
};

export default useAutoGrowTextarea;
